import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, ConfigProvider, Modal, message, theme as antdTheme } from 'antd';
import { useAuth } from '../context/AuthContext';
import { createQuickVfsProject } from '../utils/quickProjectCreation';
import { openProject } from '../utils/openProject';
import * as vfsClient from '../utils/vfsClient';
import { startService } from '../services/SandboxService';
import { resolveTenantId, validateProjectName, MAX_NAME_LENGTH } from './sandbox/NewProjectDialog';
import {
  HANDOFF_PARAM, HandoffError, consoleNextHome, existingServices, fetchManifest, forgetPending, formatSize, importService,
  parseHandoffUrl, pendingHandoff, readRecord, rememberPending, withoutHandoffParam, writeRecord,
} from '../services/ConsoleHandoffService';
import './ConsoleHandoff.css';

// The value is kept as soon as Flow loads: a redirect (sign-in, Home to Welcome, …) can drop the query string before
// this component reads it.
if (typeof window !== 'undefined') {
  const initial = new URLSearchParams(window.location.search).get(HANDOFF_PARAM);
  if (initial) { rememberPending(initial); }
}

// One run per hand-off id per page (React may mount twice; a second click on the same link must not import twice).
const running = new Map();

const PHASES = {
  waiting: 'Waiting',
  importing: 'Importing',
  downloading: 'Downloading from Console Next',
  uploading: 'Uploading to your sandbox',
  done: 'Imported',
  existed: 'Already in the project',
  failed: 'Failed',
};

const minutesLeft = (iso) => Math.max(0, Math.round((Date.parse(iso) - Date.now()) / 60000));

/**
 * Open in Flow, the receiving side (see services/ConsoleHandoffService.js): when Flow is opened with
 * `?consoleHandoff=<manifest URL>` from Console Next, ask, then create a project in the person's sandbox with one
 * service per repository, open it and start the previews. Opening the same link again resumes or just opens the
 * project. Nothing happens without the person's own Flow session and their confirmation.
 */
export default function ConsoleHandoff() {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(null); // { step, handoff, manifest, error, record }
  const [name, setName] = useState('');
  const [progress, setProgress] = useState({}); // service name -> { phase, error }
  const started = useRef(null);

  const raw = new URLSearchParams(location.search).get(HANDOFF_PARAM) || pendingHandoff();

  const clearParam = useCallback(() => {
    forgetPending();
    if (new URLSearchParams(location.search).has(HANDOFF_PARAM)) {
      navigate(withoutHandoffParam(location.pathname, location.search, location.hash), { replace: true });
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  const close = useCallback(() => {
    setState(null);
    clearParam();
  }, [clearParam]);

  // The project's Dashboard (its services and their previews), as picking it from the list opens it. The Console link is
  // replaced in the history first, so Back does not come back to it.
  const openDashboard = useCallback((record) => {
    setState(null);
    clearParam();
    openProject({ project: { _id: record.projectId, name: record.name, tenantId: record.tenantId }, navigate });
  }, [clearParam, navigate]);

  // Read the hand-off once the person is signed in.
  useEffect(() => {
    if (!raw || !isAuthenticated || !user || started.current === raw) { return; }
    started.current = raw;
    rememberPending(raw);
    let handoff;
    try {
      handoff = parseHandoffUrl(raw);
    } catch (error) {
      setState({ step: 'refused', error });
      return;
    }
    const record = readRecord(handoff.id);
    if (record?.complete && record.projectId) {
      openDashboard(record);
      return;
    }
    setState({ step: 'loading', handoff, record });
    fetchManifest(handoff).then((manifest) => {
      setName(record?.name || manifest.name);
      setState({ step: 'confirm', handoff, manifest, record });
    }).catch((error) => {
      setState({ step: error?.kind === 'expired' ? 'expired' : 'refused', handoff, error, record });
    });
  }, [raw, isAuthenticated, user, openDashboard]);

  const run = async() => {
    const { handoff, manifest } = state;
    if (running.has(handoff.id)) { return; }
    running.set(handoff.id, true);
    setState((current) => ({ ...current, step: 'importing', error: null }));
    setProgress(Object.fromEntries(manifest.services.map((service) => [service.name, { phase: 'waiting' }])));
    let record = readRecord(handoff.id) || { services: {} };
    try {
      if (!record.projectId) {
        const tenantId = await resolveTenantId();
        if (!tenantId) { throw new Error('Your account has no workspace to create projects in yet.'); }
        const projectName = name.trim();
        const created = await createQuickVfsProject(tenantId, projectName, null, { registerDefaultService: false });
        record = { ...record, projectId: created.projectId, tenantId, name: created.name || projectName, from: manifest.from, services: {} };
        writeRecord(handoff.id, record);
      }
      const present = await existingServices(record.projectId);
      const imported = [];
      for (const service of manifest.services) {
        const already = record.services?.[service.name] === 'done' || present.has(service.name);
        if (already) {
          vfsClient.registerVfsService(record.projectId, service.subfolder);
          setProgress((p) => ({ ...p, [service.name]: { phase: 'existed' } }));
          imported.push(service);
          continue;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          await importService(record.projectId, service, {
            onPhase: (phase) => setProgress((p) => ({ ...p, [service.name]: { phase } })),
          });
          vfsClient.registerVfsService(record.projectId, service.subfolder);
          record = { ...record, services: { ...record.services, [service.name]: 'done' } };
          writeRecord(handoff.id, record);
          setProgress((p) => ({ ...p, [service.name]: { phase: 'done' } }));
          imported.push(service);
        } catch (error) {
          const text = error instanceof HandoffError && error.kind === 'expired'
            ? 'The hand-off expired before this service was imported.'
            : (error?.message || 'Importing failed.');
          setProgress((p) => ({ ...p, [service.name]: { phase: 'failed', error: text } }));
        }
      }
      const failed = manifest.services.length - imported.length;
      if (failed > 0) {
        setState((current) => ({ ...current, step: 'partial', record }));
        return;
      }
      // Previews: started where the plan allows; a refusal (e.g. too many running) is said, not fatal.
      const starts = await Promise.allSettled(imported.map((service) => Promise.race([
        startService(record.projectId, service.subfolder),
        new Promise((resolve) => { setTimeout(resolve, 8000); }),
      ])));
      const notStarted = starts.map((result, index) => (result.status === 'rejected' ? `${imported[index].name}: ${result.reason?.message || 'not started'}` : null)).filter(Boolean);
      record = { ...record, complete: true };
      writeRecord(handoff.id, record);
      openDashboard(record);
      const count = `${imported.length} service${imported.length === 1 ? '' : 's'}`;
      if (notStarted.length) {
        message.warning(`Imported ${count} from Console Next. Not started: ${notStarted.join('; ')}`, 8);
      } else {
        message.success(`Imported ${count} from Console Next. The previews are starting.`, 5);
      }
    } catch (error) {
      setState((current) => ({ ...current, step: 'confirm', error, record }));
    } finally {
      running.delete(handoff.id);
    }
  };

  if (!state || state.step === 'loading' && !state.handoff) { return null; }

  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const consoleHome = consoleNextHome();
  const backLink = consoleHome ? <a href={consoleHome} rel="noopener noreferrer" target="_blank">Back to Console Next</a> : null;
  const { step, manifest, handoff, error, record } = state;
  const nameError = step === 'confirm' && !record?.projectId ? validateProjectName(name) : null;
  const busy = step === 'importing';

  let body;
  let footer = null;
  if (step === 'loading') {
    body = <p className="ch-note">Reading the hand-off from Console Next…</p>;
  } else if (step === 'refused') {
    body = <Alert showIcon title={error?.message || 'Flow could not read this hand-off.'} type="error" />;
    footer = [<Button key="close" onClick={close}>Close</Button>];
  } else if (step === 'expired') {
    body = (
      <>
        <Alert
          description={<>Console Next keeps a hand-off for 15 minutes. Open it in Flow again from Console Next. {backLink}</>}
          showIcon
          title="This hand-off has expired"
          type="warning"
        />
        {record?.projectId && <p className="ch-note">It was already imported in part into “{record.name}”.</p>}
      </>
    );
    footer = [
      record?.projectId && <Button key="open" onClick={() => openDashboard(record)}>Open “{record.name}”</Button>,
      <Button key="close" onClick={close} type="primary">Close</Button>,
    ].filter(Boolean);
  } else {
    const total = manifest.services.reduce((sum, service) => sum + (service.bytes || 0), 0);
    body = (
      <>
        <p className="ch-note">
          {manifest.from || 'Console Next'} from Console Next at <b>{new URL(handoff.origin).host}</b>
          {manifest.expiresAt ? ` · expires in ${minutesLeft(manifest.expiresAt)} min` : ''}
        </p>
        {record?.projectId ? (
          <p className="ch-note">Continues the project “{record.name}” started from this hand-off.</p>
        ) : (
          <div className="ch-field">
            <label htmlFor="ch-project-name">Project name</label>
            <input
              aria-invalid={nameError ? 'true' : 'false'}
              disabled={busy}
              id="ch-project-name"
              maxLength={MAX_NAME_LENGTH}
              onChange={(event) => setName(event.target.value)}
              value={name}
            />
            {nameError && <span className="ch-field-error">{nameError}</span>}
          </div>
        )}
        <ul aria-label="Services" className="ch-services">
          {manifest.services.map((service) => {
            const p = progress[service.name];
            const size = formatSize(service.bytes);
            return (
              <li className={`ch-service${p ? ` ch-${p.phase}` : ''}`} key={service.name}>
                <code className="ch-service-path">{service.subfolder}</code>
                <span className="ch-service-meta">
                  from {service.repository}
                  {service.files !== null ? ` · ${service.files} file${service.files === 1 ? '' : 's'}` : ''}
                  {size ? ` · ${size}` : ''}
                </span>
                {p && <span className="ch-service-phase" role="status">{PHASES[p.phase] || p.phase}{p.phase === 'importing' || p.phase === 'downloading' || p.phase === 'uploading' ? '…' : ''}</span>}
                {p?.error && <span className="ch-service-error">{p.error}</span>}
              </li>
            );
          })}
        </ul>
        {total > 0 && manifest.services.length > 1 && <p className="ch-note">{formatSize(total)} in all.</p>}
        {manifest.skipped.length > 0 && <Alert showIcon title={`Console Next could not read: ${manifest.skipped.join('; ')}`} type="warning" />}
        {error && <Alert showIcon title={error.message || 'The project could not be created. Try again.'} type="error" />}
        {step === 'partial' && <Alert showIcon title="Some services were not imported. Try them again, or open the project as it is." type="warning" />}
        <p className="ch-note">A new project in your sandbox, one service per repository. Nothing is written back to Git.</p>
      </>
    );
    footer = step === 'partial' ? [
      <Button key="open" onClick={() => openDashboard({ ...record, complete: false })}>Open the project</Button>,
      <Button key="retry" onClick={run} type="primary">Try again</Button>,
    ] : [
      <Button disabled={busy} key="cancel" onClick={close}>Cancel</Button>,
      <Button disabled={Boolean(nameError)} key="go" loading={busy} onClick={run} type="primary">
        {busy ? 'Importing…' : record?.projectId ? 'Continue the import' : 'Create project and import'}
      </Button>,
    ];
  }

  return (
    <ConfigProvider theme={{ algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
      <Modal
        className="console-handoff"
        closable={!busy}
        footer={footer}
        mask={{ closable: false }}
        onCancel={busy ? undefined : close}
        open
        title="Open in Flow from Console Next"
        width={560}
      >
        {body}
      </Modal>
    </ConfigProvider>
  );
}
